/**
 * Email signup and recovery callback bridge (web only).
 *
 * Why it exists: the signup edge function uses plain createClient.signUp, so the confirmation link
 * returns the IMPLICIT shape `/#access_token=…&refresh_token=…&type=signup`, while the web client
 * (createBrowserClient) is PKCE. The SDK recognises the fragment, refuses it with
 * AuthPKCEGrantCodeExchangeError, and leaves live tokens in the address bar without a session.
 * This adopts such a link through the SDK's own supported setSession (server-validated, cookie
 * adapter writes normally). Rejected alternatives: global flowType 'implicit' (weakens Google PKCE),
 * hand-written cookies, a fake verifier. Revisit with a token_hash mail template (see DECISIONS.md).
 *
 * Invariants: never inside onAuthStateChange (it holds the auth lock; setSession would deadlock);
 * raw tokens live only in this module's closure and are never logged/stored/put in React state; an
 * existing session always wins; the final session/epoch check and the setSession call are
 * synchronous neighbours, so only the SDK's own lock-protected write remains after the decision.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { FLAGS } from '@/config/featureFlags';

export type EmailSignupOutcome =
  | 'none' // not a bridgeable callback, or the SDK handled/failed it itself: leave it alone
  | 'adopted'
  | 'existing_session' // another actor is signed in: never switch accounts silently
  | 'failed' // token rejected by the server or setSession failed
  | 'superseded'; // the learner did a login/signup/sign-out meanwhile: they win silently

export const BRIDGE_FAILED_COPY = 'We couldn’t finish signing you in. Sign in to continue.';
export const BRIDGE_EXISTING_SESSION_COPY =
  'You’re already signed in to an account. Sign out first, then open the email link again.';

const ALLOWED_KEYS = new Set([
  'access_token',
  'refresh_token',
  'expires_in',
  'expires_at',
  'token_type',
  'type',
]);
const REQUIRED_ONCE = ['access_token', 'refresh_token', 'expires_in', 'type', 'token_type'];
const MAX_EXPIRES_SECONDS = 7 * 24 * 3600;
const MAX_EXPIRES_AT = 4_102_444_800; // year 2100, as epoch seconds

/** Strict parse of the implicit signup fragment; null for anything else (incl. mixed callbacks). */
function parseEmailCallback(
  search: string,
  hash: string,
  purpose: 'signup' | 'recovery',
): { access_token: string; refresh_token: string } | null {
  const query = new URLSearchParams(search);
  for (const key of query.keys()) if (key === 'code' || key.startsWith('error')) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  for (const key of params.keys()) if (!ALLOWED_KEYS.has(key)) return null;
  for (const key of REQUIRED_ONCE) {
    const all = params.getAll(key);
    if (all.length !== 1 || all[0] === '') return null;
  }
  if (params.getAll('expires_at').length > 1) return null;
  if (params.get('type') !== purpose || params.get('token_type') !== 'bearer') return null;
  const numeric = (v: string | null, max: number) =>
    v !== null && /^\d+$/.test(v) && Number(v) > 0 && Number(v) <= max;
  if (!numeric(params.get('expires_in'), MAX_EXPIRES_SECONDS)) return null;
  if (params.has('expires_at') && !numeric(params.get('expires_at'), MAX_EXPIRES_AT)) return null;
  return { access_token: params.get('access_token')!, refresh_token: params.get('refresh_token')! };
}

/** Signup remains strict: recovery tokens are never accepted by this parser. */
export const parseSignupCallback = (search: string, hash: string) =>
  parseEmailCallback(search, hash, 'signup');

/** Recovery has a separate purpose and a single allowed destination. */
export const parseRecoveryCallback = (search: string, hash: string, pathname: string) =>
  pathname === '/reset-password' ? parseEmailCallback(search, hash, 'recovery') : null;

/** Credential-free classification for AuthProvider; token values stay in this module. */
export const hasRecoveryCallback = () =>
  parseRecoveryCallback(window.location.search, window.location.hash, window.location.pathname) !==
  null;
let adoptedRecoveryUserId: string | null = null;
export const getAdoptedRecoveryUserId = () => adoptedRecoveryUserId;

// Bumped by every learner-initiated auth action; a bridge that started before a bump yields.
let actionEpoch = 0;
let committing = false;
export const getAuthActionEpoch = () => actionEpoch;
export const noteUserAuthAction = () => void actionEpoch++;
/** An adopted account/sign-out also supersedes a pending link, including another tab's event. */
export const noteAuthActorAdoption = () => void actionEpoch++;
/** True only during the final setSession; manual auth actions refuse rather than race it. */
export const isEmailSignupCommitting = () => committing;

let flight: Promise<EmailSignupOutcome> | null = null;
export function resetEmailSignupBridgeForTests() {
  flight = null;
  actionEpoch = 0;
  committing = false;
  adoptedRecoveryUserId = null;
}

/** Module-scoped singleflight: StrictMode double effects and remounts share one run per page load. */
export function runEmailSignupBridge(client: SupabaseClient): Promise<EmailSignupOutcome> {
  flight ??= bridge(client);
  return flight;
}

async function bridge(client: SupabaseClient): Promise<EmailSignupOutcome> {
  if (FLAGS.nativeIOS) return 'none';
  const recoveryTokens = parseRecoveryCallback(
    window.location.search,
    window.location.hash,
    window.location.pathname,
  );
  const tokens =
    recoveryTokens ?? parseSignupCallback(window.location.search, window.location.hash);
  if (!tokens) return 'none';
  const epoch = actionEpoch;

  // The SDK must see the hash first: its refusal is how we know it did not adopt the link itself.
  const { error: initError } = await client.auth.initialize();
  if (initError?.name !== 'AuthPKCEGrantCodeExchangeError') return 'none';

  // Scrub before any further network call; tokens now live only in `tokens`.
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + window.location.search,
  );

  try {
    // A child effect may have started manual auth before this parent's mount effect.
    // That request owns the page even if it has not returned a session yet.
    if (epoch !== 0 || actionEpoch !== epoch) return 'superseded';
    if ((await client.auth.getSession()).data.session) return 'existing_session';
    const { data, error } = await client.auth.getUser(tokens.access_token);
    if (error || !data.user) return 'failed';

    const current = (await client.auth.getSession()).data.session;
    if (actionEpoch !== epoch) return 'superseded';
    if (current) return current.user.id === data.user.id ? 'superseded' : 'existing_session';

    committing = true; // no await between the checks above and this line
    try {
      const { data: adopted, error: setError } = await client.auth.setSession(tokens);
      if (setError) return 'failed';
      if (recoveryTokens) {
        // The validated actor must also be the actor returned by SDK adoption.
        if (adopted.session?.user.id !== data.user.id) return 'failed';
        adoptedRecoveryUserId = data.user.id;
      }
      return 'adopted';
    } finally {
      committing = false;
    }
  } catch {
    return 'failed';
  }
}
